/**
 * ProviderChoiceModal — the shared connect modal (Story MC6 AC2/AC3).
 *
 * Opened from BOTH the Kommunikation zero-state CTA and the Postfächer page.
 * Two provider cards (Microsoft 365 / Outlook · Google Workspace / Gmail); each
 * card → `useConnectMailbox(provider)` → `window.location.assign(authorize_url)`.
 * The clicked card goes pending+disabled during the POST (no double-submit).
 *
 * Under the Google card: the ALWAYS-visible whitelist disclaimer (Google Testing
 * mode ≤100 users, mc3 deploy notes) + a "Zugang anfragen" `mailto:` with a
 * pre-filled German subject/body and three [BITTE AUSFÜLLEN] placeholders.
 *
 * When availability is `disabled` (creds not seeded / MAILBOX_CONNECT_ENABLED=false)
 * the cards render a disabled "in Kürze verfügbar" state — no broken connect button.
 *
 * [Source: apps/web/src/features/administration/components.tsx (Dialog)]
 * [Source: docs/stories/mc3-google-driver.md AC6 + §deploy — Testing-mode caveat]
 */

import { Dialog } from '../../administration/components';
import { Button } from '../../../components/ui/Button';
import { ProviderMark, providerLabel } from './ProviderMark';
import { useConnectMailbox } from '../hooks/useMailboxConnections';
import type { MailboxAvailability } from '../hooks/useMailboxConnections';
import type { MailboxProvider } from '../types';

/** The pre-filled Gmail-whitelist request mail (built with encodeURIComponent). */
export function buildGoogleAccessMailto(): string {
  const subject = 'Brokly Gmail-Zugang anfragen';
  const body = [
    'Hallo Valerio,',
    '',
    'ich möchte mein Google-/Gmail-Postfach mit Brokly verbinden und bitte um Freischaltung (Whitelisting) für den Zugang.',
    '',
    'Name: [BITTE AUSFÜLLEN: Ihr Name]',
    'Firma: [BITTE AUSFÜLLEN: Ihre Firma]',
    'Freizuschaltende Gmail-Adresse: [BITTE AUSFÜLLEN: ihre.adresse@gmail.com]',
    '',
    'Besten Dank und freundliche Grüsse',
  ].join('\n');
  return `mailto:valerio.job@brokly.ch?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function ProviderCard({
  provider,
  disabled,
  pending,
  onConnect,
}: {
  provider: MailboxProvider;
  disabled: boolean;
  pending: boolean;
  onConnect: (provider: MailboxProvider) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled || pending}
      onClick={() => onConnect(provider)}
      className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ProviderMark provider={provider} />
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-medium text-foreground">{providerLabel(provider)}</span>
        <span className="text-xs text-muted-foreground">
          {disabled ? 'In Kürze verfügbar' : pending ? 'Weiterleitung …' : 'Verbinden'}
        </span>
      </span>
    </button>
  );
}

export function ProviderChoiceModal({
  open,
  onClose,
  availability,
}: {
  open: boolean;
  onClose: () => void;
  /** From `useMailboxConnections().availability` — `disabled` ⇒ the coming-soon cards. */
  availability: MailboxAvailability;
}) {
  const connect = useConnectMailbox();
  const disabled = availability === 'disabled';
  const mailtoHref = buildGoogleAccessMailto();

  const onConnect = (provider: MailboxProvider) => {
    if (disabled) return;
    connect.mutate(provider);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Postfach verbinden"
      description="Verbinde dein persönliches E-Mail-Postfach, um Nachrichten direkt in Brokly zu bearbeiten und zu versenden."
    >
      <div className="flex flex-col gap-3">
        {disabled && (
          <div className="rounded-md border border-status-info-border bg-status-info-bg/40 px-3 py-2 text-xs text-status-info">
            Die Postfach-Verbindung wird in Kürze freigeschaltet.
          </div>
        )}

        <ProviderCard
          provider="microsoft"
          disabled={disabled}
          pending={connect.pendingProvider === 'microsoft'}
          onConnect={onConnect}
        />

        <div className="flex flex-col gap-1.5">
          <ProviderCard
            provider="google"
            disabled={disabled}
            pending={connect.pendingProvider === 'google'}
            onConnect={onConnect}
          />
          {/* Always-visible Google whitelist disclaimer + Zugang-anfragen mailto (AC3). */}
          <p className="px-1 text-xs text-muted-foreground">
            Die Verbindung mit Google/Gmail ist zurzeit nur nach manueller Freischaltung möglich
            (Google-Testmodus). Ohne vorherige Freischaltung funktioniert die Verbindung nicht.{' '}
            <a
              href={mailtoHref}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Zugang anfragen
            </a>
          </p>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button variant="ghost" onClick={onClose}>
          Abbrechen
        </Button>
      </div>
    </Dialog>
  );
}
